import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { 
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, AreaChart, Area, CartesianGrid, Legend, LineChart, Line
} from 'recharts';
import { toast } from '../lib/toast';
import Sidebar from '../components/layout/Sidebar';
import { 
  DollarSign, TrendingUp, TrendingDown, Package, Truck, CreditCard, 
  Building2, Wallet, PiggyBank, BarChart3, Calendar, RefreshCw, 
  ChevronDown, ChevronRight, ShoppingCart, Users, FileText, ArrowUpRight, ArrowDownRight,
  Banknote, Receipt, Store, X
} from 'lucide-react';

const COLORS = ['#f97316', '#10b981', '#6366f1', '#ec4899', '#3b82f6', '#eab308', '#8b5cf6'];

const FinancialReports = () => {
  const { api } = useAuth();
  const { currentBranch, isMatriz } = useBranch();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('compras'); // compras, ventas, flujo, balance
  const [activeSubTab, setActiveSubTab] = useState('proveedor');
  
  // Date filters
  const [period, setPeriod] = useState('month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const tabsContainerRef = useRef(null);

  // Helper functions - header is sticky so tabs always visible
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSelectedItem(null);
  };

  const handlePeriodChange = (p) => {
    setPeriod(p);
  };
  
  // Report data states
  const [purchasesBySupplier, setPurchasesBySupplier] = useState(null);
  const [purchasesByMethod, setPurchasesByMethod] = useState(null);
  const [purchasesBySource, setPurchasesBySource] = useState(null);
  const [purchasesByPeriod, setPurchasesByPeriod] = useState(null);
  const [topSupplies, setTopSupplies] = useState(null);
  const [salesByMethod, setSalesByMethod] = useState(null);
  const [cashFlow, setCashFlow] = useState(null);
  const [cajaChica, setCajaChica] = useState(null);
  const [expensesByCategory, setExpensesByCategory] = useState(null);
  const [profitability, setProfitability] = useState(null);
  const [monthlyComparison, setMonthlyComparison] = useState(null);
  const [balanceGeneral, setBalanceGeneral] = useState(null);
  
  // Detail panel
  const [selectedItem, setSelectedItem] = useState(null);
  const [expandedSuppliers, setExpandedSuppliers] = useState({});

  const buildQueryParams = () => {
    let params = `period=${period}`;
    if (period === 'custom' && startDate && endDate) {
      params += `&start_date=${startDate}&end_date=${endDate}`;
    }
    // Add branch filter
    if (currentBranch) {
      params += `&branch_id=${currentBranch.branch_id}&is_matriz=${isMatriz}`;
    }
    return params;
  };

  const fetchReportData = async () => {
    setLoading(true);
    const params = buildQueryParams();
    
    try {
      // Fetch all reports in parallel based on active tab
      if (activeTab === 'compras') {
        const [supplierRes, methodRes, sourceRes, periodRes, topRes] = await Promise.all([
          api.get(`/reports/financial/purchases/by-supplier?${params}`),
          api.get(`/reports/financial/purchases/by-payment-method?${params}`),
          api.get(`/reports/financial/purchases/by-source?${params}`),
          api.get(`/reports/financial/purchases/by-period?${params}&group_by=day`),
          api.get(`/reports/financial/purchases/top-supplies?${params}&limit=15`)
        ]);
        setPurchasesBySupplier(supplierRes.data);
        setPurchasesByMethod(methodRes.data);
        setPurchasesBySource(sourceRes.data);
        setPurchasesByPeriod(periodRes.data);
        setTopSupplies(topRes.data);
      } else if (activeTab === 'ventas') {
        const [salesMethodRes] = await Promise.all([
          api.get(`/reports/financial/sales/by-payment-method?${params}`)
        ]);
        setSalesByMethod(salesMethodRes.data);
      } else if (activeTab === 'flujo') {
        const [flowRes, cajaChicaRes, expensesRes] = await Promise.all([
          api.get(`/reports/financial/cash-flow?${params}`),
          api.get(`/reports/financial/caja-chica?${params}`),
          api.get(`/reports/financial/expenses-by-category?${params}`)
        ]);
        setCashFlow(flowRes.data);
        setCajaChica(cajaChicaRes.data);
        setExpensesByCategory(expensesRes.data);
      } else if (activeTab === 'balance') {
        const [profitRes, compareRes, balanceRes] = await Promise.all([
          api.get(`/reports/financial/profitability?${params}`),
          api.get('/reports/financial/monthly-comparison'),
          api.get(`/reports/financial/balance-general?${params}`)
        ]);
        setProfitability(profitRes.data);
        setMonthlyComparison(compareRes.data);
        setBalanceGeneral(balanceRes.data);
      }
    } catch (error) {
      toast.error('Error al cargar reportes');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
  }, [activeTab, period, startDate, endDate]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('es-MX', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        timeZone: 'America/Hermosillo'
      });
    } catch {
      return dateStr;
    }
  };

  const toggleSupplierExpand = (supplier) => {
    setExpandedSuppliers(prev => ({
      ...prev,
      [supplier]: !prev[supplier]
    }));
  };

  // ==================== RENDER SECTIONS ====================

  const renderPeriodSelector = () => (
    <div className="flex flex-wrap items-center gap-3 bg-slate-50 rounded-lg p-3 mb-6">
      <div className="flex items-center gap-1 bg-white rounded-lg p-1 shadow-sm">
        {['today', 'week', 'month', 'year', 'custom'].map((p) => (
          <button
            type="button"
            key={p}
            onClick={(e) => { e.preventDefault(); handlePeriodChange(p); }}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              period === p ? 'bg-accent text-white' : 'text-muted-foreground hover:bg-slate-100'
            }`}
          >
            {p === 'today' ? 'Hoy' : p === 'week' ? 'Semana' : p === 'month' ? 'Mes' : p === 'year' ? 'Año' : 'Personalizado'}
          </button>
        ))}
      </div>
      {period === 'custom' && (
        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-auto"
          />
          <span className="text-muted-foreground">a</span>
          <Input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-auto"
          />
        </div>
      )}
      <Button variant="outline" size="icon" onClick={fetchReportData} disabled={loading}>
        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
      </Button>
    </div>
  );

  // ==================== COMPRAS TAB ====================
  const renderComprasTab = () => (
    <div className="space-y-6">
      {/* Sub-tabs */}
      <div className="flex items-center gap-2 border-b pb-2">
        {[
          { key: 'proveedor', label: 'Por Proveedor', icon: Truck },
          { key: 'metodo', label: 'Por Método de Pago', icon: CreditCard },
          { key: 'fuente', label: 'Por Fuente', icon: Wallet },
          { key: 'periodo', label: 'Por Período', icon: Calendar },
          { key: 'top', label: 'Top Insumos', icon: Package }
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveSubTab(key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
              activeSubTab === key 
                ? 'bg-accent text-white' 
                : 'text-muted-foreground hover:bg-slate-100'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Por Proveedor */}
      {activeSubTab === 'proveedor' && purchasesBySupplier && (
        <div className="flex gap-6">
          <div className={`space-y-4 transition-all duration-300 ${selectedItem ? 'w-1/2' : 'w-full'}`}>
            {/* Summary */}
            <div className="grid grid-cols-3 gap-4">
              <Card className="border-l-4 border-l-orange-500">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground">Total Compras</p>
                  <p className="text-2xl font-bold">{formatCurrency(purchasesBySupplier.summary.total_amount)}</p>
                  <p className="text-xs text-muted-foreground">{purchasesBySupplier.summary.total_purchases} compras</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-blue-500">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground">Proveedores</p>
                  <p className="text-2xl font-bold">{purchasesBySupplier.summary.total_suppliers}</p>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-green-500">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground">Promedio por Compra</p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(purchasesBySupplier.summary.total_amount / Math.max(purchasesBySupplier.summary.total_purchases, 1))}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Supplier List */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Truck className="w-4 h-4" />
                  Detalle por Proveedor
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y max-h-[500px] overflow-y-auto">
                  {purchasesBySupplier.suppliers.map((supplier, idx) => (
                    <div key={idx}>
                      <div 
                        onClick={() => {
                          toggleSupplierExpand(supplier.supplier);
                          setSelectedItem(selectedItem?.supplier === supplier.supplier ? null : supplier);
                        }}
                        className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                          selectedItem?.supplier === supplier.supplier ? 'bg-accent/10 border-l-4 border-l-accent' : ''
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-gray-400' : idx === 2 ? 'bg-orange-600' : 'bg-slate-200'
                            } text-white font-bold`}>
                              {idx + 1}
                            </div>
                            <div>
                              <p className="font-medium">{supplier.supplier}</p>
                              <p className="text-sm text-muted-foreground">{supplier.total_purchases} compras, {supplier.items_count} items</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <p className="text-lg font-bold text-orange-600">{formatCurrency(supplier.total_amount)}</p>
                            {expandedSuppliers[supplier.supplier] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </div>
                        </div>
                      </div>
                      {/* Expanded purchases */}
                      {expandedSuppliers[supplier.supplier] && (
                        <div className="bg-slate-50 px-6 py-3 space-y-2">
                          {supplier.purchases.map((purchase, pIdx) => (
                            <div key={pIdx} className="flex items-center justify-between text-sm p-2 bg-white rounded-lg">
                              <div className="flex items-center gap-2">
                                <Receipt className="w-4 h-4 text-muted-foreground" />
                                <span>{purchase.purchase_id}</span>
                                {purchase.invoice && <span className="text-muted-foreground">({purchase.invoice})</span>}
                              </div>
                              <div className="flex items-center gap-4">
                                <span className="text-muted-foreground">{formatDate(purchase.date)}</span>
                                <span className="font-medium">{formatCurrency(purchase.total)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Detail Panel */}
          {selectedItem && (
            <div className="w-1/2 bg-slate-800 rounded-xl text-white overflow-hidden">
              <div className="bg-slate-900 p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-orange-500 flex items-center justify-center">
                    <Truck className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">{selectedItem.supplier}</h3>
                    <p className="text-slate-400 text-sm">{selectedItem.total_purchases} compras en el período</p>
                  </div>
                </div>
                <button onClick={() => setSelectedItem(null)} className="p-2 hover:bg-slate-700 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-orange-400">{formatCurrency(selectedItem.total_amount)}</p>
                    <p className="text-xs text-slate-400">Total Comprado</p>
                  </div>
                  <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-400">
                      {formatCurrency(selectedItem.total_amount / Math.max(selectedItem.total_purchases, 1))}
                    </p>
                    <p className="text-xs text-slate-400">Promedio por Compra</p>
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-slate-400 uppercase tracking-wide mb-3">Últimas Compras</h4>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {selectedItem.purchases.slice(0, 10).map((p, idx) => (
                      <div key={idx} className="bg-slate-700/50 rounded-lg p-3 flex justify-between items-center">
                        <div>
                          <p className="font-medium">{p.purchase_id}</p>
                          <p className="text-xs text-slate-400">{formatDate(p.date)}</p>
                        </div>
                        <p className="font-bold text-green-400">{formatCurrency(p.total)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Por Método de Pago */}
      {activeSubTab === 'metodo' && purchasesByMethod && (
        <div className="grid lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                Distribución por Método de Pago
              </CardTitle>
              <p className="text-2xl font-bold">{formatCurrency(purchasesByMethod.summary.total_amount)}</p>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={purchasesByMethod.by_payment_method}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="total"
                      nameKey="label"
                    >
                      {purchasesByMethod.by_payment_method.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Detalle por Método</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {purchasesByMethod.by_payment_method.map((method, idx) => (
                  <div key={method.method} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                      <div>
                        <p className="font-medium">{method.label}</p>
                        <p className="text-sm text-muted-foreground">{method.count} compras</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">{formatCurrency(method.total)}</p>
                      <p className="text-sm text-muted-foreground">{((method.total / purchasesByMethod.summary.total_amount) * 100).toFixed(1)}%</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Por Fuente (Caja Principal vs Caja Chica) */}
      {activeSubTab === 'fuente' && purchasesBySource && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <Card className="border-2 border-blue-500">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Store className="w-4 h-4 text-blue-500" />
                  {purchasesBySource.caja_principal.label}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-blue-600">{formatCurrency(purchasesBySource.caja_principal.total)}</p>
                <p className="text-muted-foreground">{purchasesBySource.caja_principal.count} compras ({purchasesBySource.caja_principal.percentage}%)</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-amber-500">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <PiggyBank className="w-4 h-4 text-amber-500" />
                  {purchasesBySource.caja_chica.label}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-amber-600">{formatCurrency(purchasesBySource.caja_chica.total)}</p>
                <p className="text-muted-foreground">{purchasesBySource.caja_chica.count} compras ({purchasesBySource.caja_chica.percentage}%)</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Comparativa Visual</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={[
                    { name: 'Caja Principal', total: purchasesBySource.caja_principal.total, count: purchasesBySource.caja_principal.count },
                    { name: 'Caja Chica', total: purchasesBySource.caja_chica.total, count: purchasesBySource.caja_chica.count }
                  ]}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="name" />
                    <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Bar dataKey="total" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Por Período */}
      {activeSubTab === 'periodo' && purchasesByPeriod && (
        <div className="space-y-6">
          <div className="grid grid-cols-3 gap-4">
            <Card className="border-l-4 border-l-orange-500">
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Total del Período</p>
                <p className="text-2xl font-bold">{formatCurrency(purchasesByPeriod.summary.total_amount)}</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Compras Realizadas</p>
                <p className="text-2xl font-bold">{purchasesByPeriod.summary.total_purchases}</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-green-500">
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Promedio Diario</p>
                <p className="text-2xl font-bold">{formatCurrency(purchasesByPeriod.summary.avg_per_period)}</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Tendencia de Compras</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={purchasesByPeriod.data}>
                    <defs>
                      <linearGradient id="colorPurchases" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f97316" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Area type="monotone" dataKey="total" stroke="#f97316" strokeWidth={2} fillOpacity={1} fill="url(#colorPurchases)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Top Insumos */}
      {activeSubTab === 'top' && topSupplies && (
        <div className="grid lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-orange-500" />
                Top por Monto Gastado
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {topSupplies.top_by_amount.map((supply, idx) => (
                  <div key={supply.supply_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                        idx < 3 ? 'bg-orange-500 text-white' : 'bg-slate-200'
                      }`}>
                        {idx + 1}
                      </span>
                      <div>
                        <p className="font-medium text-sm">{supply.name}</p>
                        <p className="text-xs text-muted-foreground">{supply.purchase_count} compras</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-orange-600">{formatCurrency(supply.total_amount)}</p>
                      <p className="text-xs text-muted-foreground">{supply.total_quantity.toFixed(1)} unidades</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Package className="w-4 h-4 text-blue-500" />
                Top por Cantidad Comprada
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {topSupplies.top_by_quantity.map((supply, idx) => (
                  <div key={supply.supply_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                        idx < 3 ? 'bg-blue-500 text-white' : 'bg-slate-200'
                      }`}>
                        {idx + 1}
                      </span>
                      <div>
                        <p className="font-medium text-sm">{supply.name}</p>
                        <p className="text-xs text-muted-foreground">Costo prom: {formatCurrency(supply.avg_cost)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-blue-600">{supply.total_quantity.toFixed(1)}</p>
                      <p className="text-xs text-muted-foreground">unidades</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );

  // ==================== VENTAS TAB ====================
  const renderVentasTab = () => (
    <div className="space-y-6">
      {salesByMethod && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 gap-4">
            <Card className="border-l-4 border-l-green-500">
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Total Ventas</p>
                <p className="text-2xl font-bold">{formatCurrency(salesByMethod.summary.total_sales)}</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Total Órdenes</p>
                <p className="text-2xl font-bold">{salesByMethod.summary.total_orders}</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Ventas por Método de Pago
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={salesByMethod.by_payment_method}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey="total"
                        nameKey="label"
                      >
                        {salesByMethod.by_payment_method.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Detalle por Método</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {salesByMethod.by_payment_method.map((method, idx) => (
                    <div key={method.method} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-4 h-4 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                        <div>
                          <p className="font-medium">{method.label}</p>
                          <p className="text-sm text-muted-foreground">{method.count} órdenes</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-green-600">{formatCurrency(method.total)}</p>
                        <p className="text-sm text-muted-foreground">{method.percentage}%</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );

  // ==================== FLUJO TAB ====================
  const renderFlujoTab = () => (
    <div className="space-y-6">
      {/* Cash Flow Summary */}
      {cashFlow && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-l-4 border-l-green-500">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <ArrowUpRight className="w-4 h-4 text-green-500" />
                  <span className="text-xs text-muted-foreground">Ingresos</span>
                </div>
                <p className="text-xl font-bold text-green-600">{formatCurrency(cashFlow.summary.total_income)}</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-red-500">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <ArrowDownRight className="w-4 h-4 text-red-500" />
                  <span className="text-xs text-muted-foreground">Egresos</span>
                </div>
                <p className="text-xl font-bold text-red-600">{formatCurrency(cashFlow.summary.total_expenses)}</p>
              </CardContent>
            </Card>
            <Card className={`border-l-4 ${cashFlow.summary.net_flow >= 0 ? 'border-l-blue-500' : 'border-l-orange-500'}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className={`w-4 h-4 ${cashFlow.summary.net_flow >= 0 ? 'text-blue-500' : 'text-orange-500'}`} />
                  <span className="text-xs text-muted-foreground">Flujo Neto</span>
                </div>
                <p className={`text-xl font-bold ${cashFlow.summary.net_flow >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                  {formatCurrency(cashFlow.summary.net_flow)}
                </p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-slate-400">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <BarChart3 className="w-4 h-4 text-slate-500" />
                  <span className="text-xs text-muted-foreground">Prom. Diario</span>
                </div>
                <p className="text-xl font-bold">{formatCurrency(cashFlow.summary.avg_daily_income)}</p>
              </CardContent>
            </Card>
          </div>

          {/* Cash Flow Chart */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Flujo de Caja Diario</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={cashFlow.data}>
                    <defs>
                      <linearGradient id="colorIncome" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorExpenses" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    <Area type="monotone" dataKey="income" name="Ingresos" stroke="#10b981" fillOpacity={1} fill="url(#colorIncome)" />
                    <Area type="monotone" dataKey="expenses" name="Egresos" stroke="#ef4444" fillOpacity={1} fill="url(#colorExpenses)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Caja Chica Report */}
      {cajaChica && (
        <Card className="border-2 border-amber-300">
          <CardHeader className="pb-2 bg-amber-50">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <PiggyBank className="w-5 h-5 text-amber-600" />
              Reporte de Caja Chica
            </CardTitle>
            <p className="text-3xl font-bold text-amber-600">{formatCurrency(cajaChica.current_balance)}</p>
            <p className="text-sm text-muted-foreground">Saldo Actual</p>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="p-3 bg-green-50 rounded-lg text-center">
                <p className="text-xs text-green-600">Depósitos</p>
                <p className="text-lg font-bold text-green-700">{formatCurrency(cajaChica.summary.total_deposits)}</p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg text-center">
                <p className="text-xs text-red-600">Gastos</p>
                <p className="text-lg font-bold text-red-700">{formatCurrency(cajaChica.summary.total_expenses)}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg text-center">
                <p className="text-xs text-muted-foreground">Movimientos</p>
                <p className="text-lg font-bold">{cajaChica.summary.movements_count}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Expenses by Category */}
      {expensesByCategory && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Egresos por Categoría
            </CardTitle>
            <p className="text-2xl font-bold text-red-600">{formatCurrency(expensesByCategory.summary.total_expenses)}</p>
          </CardHeader>
          <CardContent>
            <div className="grid lg:grid-cols-2 gap-4">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={expensesByCategory.by_category}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="total"
                      nameKey="label"
                    >
                      {expensesByCategory.by_category.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2">
                {expensesByCategory.by_category.map((cat, idx) => (
                  <div key={cat.category} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                      <span className="text-sm font-medium">{cat.label}</span>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">{formatCurrency(cat.total)}</p>
                      <p className="text-xs text-muted-foreground">{cat.percentage}%</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // ==================== BALANCE TAB ====================
  const renderBalanceTab = () => (
    <div className="space-y-6">
      {/* Monthly Comparison */}
      {monthlyComparison && (
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-blue-600" />
              Comparativo Mensual
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-4 bg-white rounded-lg shadow-sm">
                <p className="text-xs text-muted-foreground mb-1">Ventas {monthlyComparison.current_month.label}</p>
                <p className="text-xl font-bold">{formatCurrency(monthlyComparison.current_month.sales)}</p>
                <p className={`text-xs flex items-center gap-1 ${monthlyComparison.changes.sales >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {monthlyComparison.changes.sales >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                  {monthlyComparison.changes.sales}% vs mes anterior
                </p>
              </div>
              <div className="p-4 bg-white rounded-lg shadow-sm">
                <p className="text-xs text-muted-foreground mb-1">Órdenes</p>
                <p className="text-xl font-bold">{monthlyComparison.current_month.orders}</p>
                <p className={`text-xs flex items-center gap-1 ${monthlyComparison.changes.orders >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {monthlyComparison.changes.orders >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                  {monthlyComparison.changes.orders}%
                </p>
              </div>
              <div className="p-4 bg-white rounded-lg shadow-sm">
                <p className="text-xs text-muted-foreground mb-1">Compras</p>
                <p className="text-xl font-bold">{formatCurrency(monthlyComparison.current_month.purchases)}</p>
                <p className={`text-xs flex items-center gap-1 ${monthlyComparison.changes.purchases <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {monthlyComparison.changes.purchases >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                  {monthlyComparison.changes.purchases}%
                </p>
              </div>
              <div className="p-4 bg-white rounded-lg shadow-sm">
                <p className="text-xs text-muted-foreground mb-1">Ticket Promedio</p>
                <p className="text-xl font-bold">{formatCurrency(monthlyComparison.current_month.avg_ticket)}</p>
                <p className={`text-xs flex items-center gap-1 ${monthlyComparison.changes.avg_ticket >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {monthlyComparison.changes.avg_ticket >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                  {monthlyComparison.changes.avg_ticket}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Profitability */}
      {profitability && (
        <div className="grid lg:grid-cols-3 gap-6">
          <Card className="border-l-4 border-l-green-500">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-green-500" />
                Ingresos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-green-600">{formatCurrency(profitability.income.total_sales)}</p>
              <p className="text-sm text-muted-foreground">{profitability.income.orders_count} órdenes</p>
              <p className="text-sm text-muted-foreground">Ticket promedio: {formatCurrency(profitability.income.avg_ticket)}</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-red-500">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <ShoppingCart className="w-4 h-4 text-red-500" />
                Costos y Gastos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-red-600">{formatCurrency(profitability.expenses.total)}</p>
              <div className="mt-2 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Costo de producto:</span>
                  <span>{formatCurrency(profitability.costs.product_costs)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Compras:</span>
                  <span>{formatCurrency(profitability.expenses.purchases)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Gastos operativos:</span>
                  <span>{formatCurrency(profitability.expenses.operating_expenses)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={`border-l-4 ${profitability.profit.net_profit >= 0 ? 'border-l-blue-500' : 'border-l-orange-500'}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <TrendingUp className={`w-4 h-4 ${profitability.profit.net_profit >= 0 ? 'text-blue-500' : 'text-orange-500'}`} />
                Utilidad
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className={`text-3xl font-bold ${profitability.profit.net_profit >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                {formatCurrency(profitability.profit.net_profit)}
              </p>
              <div className="mt-2 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Utilidad bruta:</span>
                  <span className="text-green-600">{formatCurrency(profitability.profit.gross_profit)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Margen bruto:</span>
                  <span>{profitability.costs.gross_margin}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Margen neto:</span>
                  <span className={profitability.profit.net_margin >= 0 ? 'text-blue-600' : 'text-red-600'}>
                    {profitability.profit.net_margin}%
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Balance General */}
      {balanceGeneral && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Balance General del Período
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Income Details */}
              <div className="space-y-4">
                <h4 className="font-medium text-green-700 flex items-center gap-2">
                  <ArrowUpRight className="w-4 h-4" />
                  INGRESOS: {formatCurrency(balanceGeneral.income.total)}
                </h4>
                <div className="pl-4 space-y-2">
                  <p className="text-sm text-muted-foreground">{balanceGeneral.income.orders_count} órdenes completadas</p>
                  <div className="space-y-1">
                    {Object.entries(balanceGeneral.income.by_payment_method || {}).map(([method, amount]) => (
                      <div key={method} className="flex justify-between text-sm p-2 bg-green-50 rounded">
                        <span className="capitalize">{method}</span>
                        <span className="font-medium">{formatCurrency(amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Expenses Details */}
              <div className="space-y-4">
                <h4 className="font-medium text-red-700 flex items-center gap-2">
                  <ArrowDownRight className="w-4 h-4" />
                  EGRESOS: {formatCurrency(balanceGeneral.expenses.total)}
                </h4>
                <div className="pl-4 space-y-2">
                  <div className="flex justify-between text-sm p-2 bg-red-50 rounded">
                    <span>Compras de insumos</span>
                    <span className="font-medium">{formatCurrency(balanceGeneral.expenses.purchases)}</span>
                  </div>
                  <div className="flex justify-between text-sm p-2 bg-red-50 rounded">
                    <span>Gastos operativos</span>
                    <span className="font-medium">{formatCurrency(balanceGeneral.expenses.operating)}</span>
                  </div>
                  <div className="flex justify-between text-sm p-2 bg-orange-50 rounded">
                    <span>Costo de producto</span>
                    <span className="font-medium">{formatCurrency(balanceGeneral.expenses.product_costs)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Summary */}
            <div className="mt-6 pt-4 border-t grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-3 bg-green-100 rounded-lg text-center">
                <p className="text-xs text-green-700">Utilidad Bruta</p>
                <p className="text-xl font-bold text-green-800">{formatCurrency(balanceGeneral.profit.gross)}</p>
              </div>
              <div className={`p-3 rounded-lg text-center ${balanceGeneral.profit.net >= 0 ? 'bg-blue-100' : 'bg-red-100'}`}>
                <p className={`text-xs ${balanceGeneral.profit.net >= 0 ? 'text-blue-700' : 'text-red-700'}`}>Utilidad Neta</p>
                <p className={`text-xl font-bold ${balanceGeneral.profit.net >= 0 ? 'text-blue-800' : 'text-red-800'}`}>
                  {formatCurrency(balanceGeneral.profit.net)}
                </p>
              </div>
              <div className="p-3 bg-amber-100 rounded-lg text-center">
                <p className="text-xs text-amber-700">Saldo Caja Chica</p>
                <p className="text-xl font-bold text-amber-800">{formatCurrency(balanceGeneral.cash.caja_chica_balance)}</p>
              </div>
              <div className="p-3 bg-slate-100 rounded-lg text-center">
                <p className="text-xs text-slate-700">Sesiones de Caja</p>
                <p className="text-xl font-bold text-slate-800">{balanceGeneral.cash.sessions_count}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex">
      <Sidebar />
      
      <main className="flex-1 md:ml-64 p-3 md:p-8 pt-16 md:pt-8">
        <div className="max-w-7xl mx-auto" style={{ minHeight: '100vh' }}>
          {/* Header + Tabs - Sticky */}
          <div className="mb-6 sticky top-0 z-40 bg-background pb-2">
            <h1 className="text-2xl md:text-3xl font-chivo font-extrabold flex items-center gap-3" data-testid="financial-reports-title">
              <BarChart3 className="w-8 h-8 text-accent" />
              Reportes Financieros
            </h1>
            <p className="text-muted-foreground text-sm">Análisis detallado de compras, pagos y finanzas</p>
          </div>

          {/* Main Tabs */}
          <div ref={tabsContainerRef} className="flex items-center gap-2 mb-6 bg-slate-100 rounded-lg p-1.5 sticky top-16 z-30">
            {[
              { key: 'compras', label: 'Compras', icon: ShoppingCart },
              { key: 'ventas', label: 'Ventas', icon: Banknote },
              { key: 'flujo', label: 'Flujo de Caja', icon: TrendingUp },
              { key: 'balance', label: 'Balance General', icon: FileText }
            ].map(({ key, label, icon: Icon }) => (
              <button
                type="button"
                key={key}
                onClick={(e) => { e.preventDefault(); handleTabChange(key); }}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === key 
                    ? 'bg-white shadow-sm text-accent' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`tab-${key}`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>

          {/* Period Selector */}
          {renderPeriodSelector()}

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-accent" />
            </div>
          )}

          {/* Tab Content */}
          {!loading && (
            <>
              {activeTab === 'compras' && renderComprasTab()}
              {activeTab === 'ventas' && renderVentasTab()}
              {activeTab === 'flujo' && renderFlujoTab()}
              {activeTab === 'balance' && renderBalanceTab()}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default FinancialReports;
